// PDF export for Programa de Cuidados - Ciudad Enfermeria
// Uses jsPDF + autoTable. Generates an institutional-branded A4 PDF.
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { CATS, INC_LABELS, EVA_TEXT, addMinutes } from "@/data/interventions";
import { computeShiftDuration } from "@/lib/duration";
import { computeWaterBalance, BALANCE_STATUS } from "@/lib/balance";
import { evaluateVital } from "@/lib/vitals-range";
import { MORSE_QUESTIONS, computeMorseTotal, morseLevel, isMorseFilled } from "@/lib/morse";

const BRAND = {
  navy: [30, 58, 138],
  blue: [29, 78, 216],
  light: [239, 246, 255],
  ink: [15, 23, 42],
  muted: [100, 116, 139],
  border: [203, 213, 225],
  white: [255, 255, 255],
};

// ---------------- helpers ----------------
function safe(v, fallback = "—") {
  if (v === undefined || v === null) return fallback;
  const s = String(v).trim();
  return s === "" ? fallback : s;
}

function hexToRgb(hex) {
  const h = hex.replace("#", "");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

// Load the logo (SVG) and rasterize to a high-res PNG data URL via canvas.
// Cached after first call.
let _logoCache = null;
async function loadLogo() {
  if (_logoCache !== null) return _logoCache;
  try {
    const res = await fetch("/assets/logo.svg", { cache: "force-cache" });
    if (!res.ok) throw new Error("logo fetch failed");
    const svgText = await res.text();
    const svgBlob = new Blob([svgText], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);
    const dataUrl = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        try {
          const size = 1024; // raster size for PDF (crisp at A4 print quality)
          const canvas = document.createElement("canvas");
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext("2d");
          ctx.clearRect(0, 0, size, size);
          ctx.drawImage(img, 0, 0, size, size);
          URL.revokeObjectURL(url);
          resolve(canvas.toDataURL("image/png"));
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = (err) => {
        URL.revokeObjectURL(url);
        reject(err);
      };
      img.src = url;
    });
    _logoCache = dataUrl;
    return _logoCache;
  } catch (e) {
    _logoCache = false; // mark as failed so we don't retry
    return null;
  }
}

function drawHeader(doc, logoDataUrl) {
  const w = doc.internal.pageSize.getWidth();
  // Gradient bar (two solid halves for crisp print)
  doc.setFillColor(...BRAND.navy);
  doc.rect(0, 0, w, 30, "F");
  doc.setFillColor(...BRAND.blue);
  doc.rect(0, 0, w / 2, 30, "F");

  // Logo (or fallback "CE" box)
  if (logoDataUrl) {
    // white rounded background plate
    doc.setFillColor(...BRAND.white);
    doc.roundedRect(10, 4, 22, 22, 3, 3, "F");
    try {
      doc.addImage(logoDataUrl, "PNG", 11, 5, 20, 20, undefined, "FAST");
    } catch (err) {
      // jsPDF may reject malformed images - log and fall back to brandless header
      console.warn("PDF logo addImage failed, continuing without logo", err);
    }
  } else {
    doc.setFillColor(...BRAND.white);
    doc.roundedRect(12, 6.5, 15, 15, 3, 3, "F");
    doc.setTextColor(...BRAND.navy);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("CE", 19.5, 15.7, { align: "center" });
  }

  // Brand text
  doc.setTextColor(...BRAND.white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("CIUDAD ENFERMERIA", 37, 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.text("San Luis Potosi", 37, 19);
  doc.setFontSize(9);
  doc.text("Programa de Cuidados de Enfermeria", 37, 24);

  // Timestamp - large white-on-blue so the PDF is identifiable in WhatsApp/preview thumbnails
  const now = new Date();
  const dateStr = now.toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const timeStr = now.toLocaleTimeString("es-MX", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  doc.setTextColor(...BRAND.white);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.text("GENERADO", w - 12, 9, { align: "right" });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(dateStr, w - 12, 17, { align: "right" });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(timeStr, w - 12, 24, { align: "right" });
}

function drawFooter(doc, pageNum, pageCount) {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  doc.setDrawColor(...BRAND.border);
  doc.setLineWidth(0.2);
  doc.line(12, h - 12, w - 12, h - 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...BRAND.muted);
  doc.text("Documento clinico - Uso interno", 12, h - 7);
  doc.text(`Pagina ${pageNum} de ${pageCount}`, w - 12, h - 7, { align: "right" });
}

function sectionHeader(doc, n, title, y) {
  const w = doc.internal.pageSize.getWidth();
  doc.setFillColor(...BRAND.blue);
  doc.circle(16, y, 3.5, "F");
  doc.setTextColor(...BRAND.white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(String(n), 16, y + 1.3, { align: "center" });

  doc.setTextColor(...BRAND.ink);
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text(title, 22, y + 1.5);

  doc.setDrawColor(...BRAND.border);
  doc.setLineWidth(0.3);
  const titleWidth = doc.getTextWidth(title);
  doc.line(22 + titleWidth + 3, y + 1, w - 12, y + 1);
  return y + 7;
}

function infoRow(doc, label, value, x, y, valueWidth = 70) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...BRAND.muted);
  doc.text(label.toUpperCase(), x, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...BRAND.ink);
  const lines = doc.splitTextToSize(safe(value), valueWidth);
  doc.text(lines, x, y + 4.5);
  return y + 5 + lines.length * 4;
}

function statBox(doc, label, value, x, y, w) {
  doc.setDrawColor(...BRAND.border);
  doc.setLineWidth(0.3);
  doc.roundedRect(x, y, w, 14, 2, 2, "S");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.setTextColor(...BRAND.muted);
  doc.text(label.toUpperCase(), x + 2, y + 4);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...BRAND.ink);
  doc.text(safe(value), x + 2, y + 11);
}

function ensureSpace(doc, y, needed = 30, logoDataUrl = null) {
  const h = doc.internal.pageSize.getHeight();
  if (y + needed > h - 18) {
    doc.addPage();
    drawHeader(doc, logoDataUrl);
    return 38;
  }
  return y;
}

// ---------------- main export ----------------
export async function exportProgramPDF(program, options = {}) {
  const logoDataUrl = await loadLogo();
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const w = doc.internal.pageSize.getWidth();
  const left = 12;
  const right = w - 12;

  drawHeader(doc, logoDataUrl);
  let y = 38;

  const shift = program.shift || {};
  const closing = program.closing || {};
  const interventions = program.interventions || [];

  // Patient block
  doc.setFillColor(...BRAND.light);
  doc.roundedRect(left, y, right - left, 22, 3, 3, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...BRAND.muted);
  doc.text("PACIENTE", left + 4, y + 6);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.setTextColor(...BRAND.ink);
  doc.text(safe(shift.patient_name, "(sin nombre)"), left + 4, y + 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...BRAND.muted);
  const computedDur = computeShiftDuration(shift);
  const durLabel = computedDur ? computedDur.label : "-";
  const sameDay = shift.date && shift.end_date && shift.date === shift.end_date;
  const shiftSummary = sameDay
    ? `Fecha: ${safe(shift.date)}   |   ${safe(shift.start_time)} - ${safe(shift.end_time)}   |   Duracion: ${durLabel}`
    : `Inicio: ${safe(shift.date)} ${safe(shift.start_time)}   |   Termino: ${safe(shift.end_date)} ${safe(shift.end_time)}   |   Duracion: ${durLabel}`;
  doc.text(shiftSummary, left + 4, y + 19);
  y += 28;

  // ============ 1. Datos del turno ============
  y = sectionHeader(doc, 1, "Datos del turno", y);

  // 4 stat boxes
  const cols = 4;
  const gap = 3;
  const boxW = (right - left - gap * (cols - 1)) / cols;
  statBox(doc, "Inicio", `${safe(shift.date, "-")} ${safe(shift.start_time, "")}`.trim(), left, y, boxW);
  statBox(doc, "Termino", `${safe(shift.end_date, "-")} ${safe(shift.end_time, "")}`.trim(), left + (boxW + gap), y, boxW);
  statBox(doc, "Duracion", durLabel, left + (boxW + gap) * 2, y, boxW);
  statBox(doc, "Intervenciones", interventions.length, left + (boxW + gap) * 3, y, boxW);
  y += 18;

  // Nurses
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...BRAND.muted);
  doc.text("ENFERMERAS ASIGNADAS", left, y);
  y += 4;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...BRAND.ink);
  const nurses = (shift.nurses || []).join(",  ") || "(ninguna)";
  const nurseLines = doc.splitTextToSize(nurses, right - left);
  doc.text(nurseLines, left, y + 4);
  y += 4 + nurseLines.length * 4 + 4;

  // ============ 2. Programa de intervenciones ============
  y = ensureSpace(doc, y, 24, logoDataUrl);
  y = sectionHeader(doc, 2, "Programa de intervenciones", y);

  if (interventions.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(10);
    doc.setTextColor(...BRAND.muted);
    doc.text("(sin intervenciones programadas)", left, y + 4);
    y += 10;
  } else {
    const sorted = [...interventions].sort((a, b) =>
      (a.start_time || "").localeCompare(b.start_time || "")
    );
    const rows = sorted.map((iv) => [
      iv.start_time || "--:--",
      `${iv.duration_minutes}m`,
      iv.name,
      iv.category,
      iv.nurse || "-",
      iv.notes || "",
    ]);

    autoTable(doc, {
      startY: y,
      head: [["Hora", "Dur", "Intervencion", "Categoria", "Enfermera", "Notas"]],
      body: rows,
      theme: "grid",
      headStyles: {
        fillColor: BRAND.blue,
        textColor: BRAND.white,
        fontSize: 9,
        fontStyle: "bold",
      },
      bodyStyles: {
        fontSize: 8.5,
        textColor: BRAND.ink,
        cellPadding: 2.2,
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        0: { cellWidth: 16, fontStyle: "bold" },
        1: { cellWidth: 12 },
        2: { cellWidth: 52, fontStyle: "bold" },
        3: { cellWidth: 32 },
        4: { cellWidth: 28 },
        5: { cellWidth: "auto" },
      },
      margin: { left, right: 12 },
      didParseCell: (data) => {
        // Color the category cell
        if (data.section === "body" && data.column.index === 3) {
          const cat = data.cell.raw;
          const c = CATS[cat];
          if (c) {
            data.cell.styles.fillColor = hexToRgb(c.bg);
            data.cell.styles.textColor = hexToRgb(c.color);
            data.cell.styles.fontStyle = "bold";
          }
        }
      },
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  // ============ 3. Consumo de materiales ============
  const materials = program.materials || [];
  y = ensureSpace(doc, y, 28, logoDataUrl);
  y = sectionHeader(doc, 3, "Consumo de materiales", y);
  if (materials.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(10);
    doc.setTextColor(...BRAND.muted);
    doc.text("(sin registro)", left, y + 4);
    y += 10;
  } else {
    const totalPieces = materials.reduce((sum, m) => sum + (m.quantity || 0), 0);
    autoTable(doc, {
      startY: y,
      head: [["Material", "Cantidad", "Unidad"]],
      body: materials.map((m) => [m.name, String(m.quantity), m.unit || "pz"]),
      foot: [["TOTAL", String(totalPieces), "piezas"]],
      theme: "grid",
      headStyles: { fillColor: [16, 185, 129], textColor: BRAND.white, fontSize: 9, fontStyle: "bold" },
      bodyStyles: { fontSize: 9, textColor: BRAND.ink, cellPadding: 2 },
      footStyles: { fillColor: [240, 253, 244], textColor: [6, 95, 70], fontStyle: "bold", fontSize: 9 },
      alternateRowStyles: { fillColor: [240, 253, 244] },
      columnStyles: {
        0: { cellWidth: "auto", fontStyle: "bold" },
        1: { cellWidth: 25, halign: "center" },
        2: { cellWidth: 25, halign: "center" },
      },
      margin: { left, right: 12 },
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  // ============ 4. Notas de cierre ============
  y = ensureSpace(doc, y, 70, logoDataUrl);
  y = sectionHeader(doc, 4, "Notas de cierre del turno", y);

  // Status row
  const halfW = (right - left - gap) / 2;
  statBox(doc, "Condicion general", closing.condition, left, y, halfW);
  statBox(doc, "Estado emocional", closing.emotion, left + halfW + gap, y, halfW);
  y += 18;

  // Pain EVA + vitals row
  const evaVal = closing.eva_pain ?? 0;
  statBox(doc, "Dolor (EVA 0-10)", `${evaVal} / 10  -  ${EVA_TEXT[evaVal] || ""}`, left, y, right - left);
  y += 18;

  const vitals = closing.vitals || {};
  const vReadings = (vitals.readings || []).filter(
    (r) => r && (r.time || r.ta || r.fc || r.temp || r.spo2)
  );
  // Section title for vitals
  y = ensureSpace(doc, y, 24, logoDataUrl);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...BRAND.muted);
  doc.text("SIGNOS VITALES DEL TURNO", left, y);
  y += 3;
  if (vReadings.length > 0) {
    autoTable(doc, {
      startY: y + 2,
      head: [["Toma", "Hora", "T/A", "FC", "Temp", "SpO2"]],
      body: vReadings.map((r, i) => [
        String(i + 1),
        r.time || "-",
        r.ta || "-",
        r.fc || "-",
        r.temp || "-",
        r.spo2 || "-",
      ]),
      theme: "grid",
      headStyles: { fillColor: BRAND.blue, textColor: BRAND.white, fontSize: 9, fontStyle: "bold" },
      bodyStyles: { fontSize: 9, textColor: BRAND.ink, cellPadding: 2 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        0: { cellWidth: 16, halign: "center", fontStyle: "bold" },
        1: { cellWidth: 22, halign: "center" },
        2: { cellWidth: 28, halign: "center" },
        3: { cellWidth: 22, halign: "center" },
        4: { cellWidth: 22, halign: "center" },
        5: { cellWidth: 22, halign: "center" },
      },
      didParseCell: (data) => {
        if (data.section !== "body") return;
        const colToField = { 2: "ta", 3: "fc", 4: "temp", 5: "spo2" };
        const field = colToField[data.column.index];
        if (!field) return;
        const r = vReadings[data.row.index];
        const ev = evaluateVital(field, r?.[field]);
        if (ev && ev.level !== "normal") {
          data.cell.styles.textColor = [185, 28, 28];
          data.cell.styles.fillColor = [254, 226, 226];
          data.cell.styles.fontStyle = "bold";
        }
      },
      margin: { left, right: 12 },
    });
    y = doc.lastAutoTable.finalY + 4;
  } else if (vitals.ta || vitals.fc || vitals.temp || vitals.spo2) {
    // Legacy single-reading fallback
    const vCols2 = 4;
    const vW2 = (right - left - gap * (vCols2 - 1)) / vCols2;
    statBox(doc, "T/A (mmHg)", vitals.ta, left, y + 2, vW2);
    statBox(doc, "FC (lpm)", vitals.fc, left + (vW2 + gap), y + 2, vW2);
    statBox(doc, "Temp (C)", vitals.temp, left + (vW2 + gap) * 2, y + 2, vW2);
    statBox(doc, "SpO2 (%)", vitals.spo2, left + (vW2 + gap) * 3, y + 2, vW2);
    y += 24;
  } else {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.setTextColor(...BRAND.muted);
    doc.text("(sin registro)", left, y + 5);
    y += 10;
  }
  if (vitals.weight_kg) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...BRAND.ink);
    doc.text(`Peso del paciente: ${vitals.weight_kg} kg`, left, y + 2);
    y += 6;
  }

  // Alimentacion
  y = ensureSpace(doc, y, 36, logoDataUrl);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...BRAND.muted);
  doc.text("ALIMENTACION", left, y);
  y += 3;
  const n = closing.nutrition || {};
  const nRows = [
    ["Via", n.via],
    ["Tolerancia", n.tolerance],
    ["Consumo del platillo", n.intake],
    ["Apetito", n.appetite],
    ["Tipo de dieta", n.diet_type],
    ["Desayuno (racion)", n.breakfast],
    ["Comida (racion)", n.lunch],
    ["Cena (racion)", n.dinner],
    ["Hidratacion oral (ml)", n.water_oral_ml],
    ["Nausea", n.nausea === true ? "SI" : n.nausea === false ? "NO" : ""],
    ["Vomito", n.vomit === true ? "SI" : n.vomit === false ? "NO" : ""],
  ].filter(([, val]) => val);
  if (nRows.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.setTextColor(...BRAND.muted);
    doc.text("(sin registro)", left, y + 4);
    y += 9;
  } else {
    autoTable(doc, {
      startY: y + 2,
      body: nRows,
      theme: "grid",
      bodyStyles: { fontSize: 9, textColor: BRAND.ink, cellPadding: 1.8 },
      columnStyles: {
        0: { cellWidth: 56, fontStyle: "bold", fillColor: [241, 245, 249], textColor: BRAND.muted },
        1: { cellWidth: "auto" },
      },
      margin: { left, right: 12 },
    });
    y = doc.lastAutoTable.finalY + 5;
  }

  // Eliminacion
  y = ensureSpace(doc, y, 36, logoDataUrl);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...BRAND.muted);
  doc.text("ELIMINACION", left, y);
  y += 3;
  const el = closing.elimination || {};
  const elRows = [
    ["Miccion - Metodo", el.urine_method],
    ["Miccion - Tipo (aspecto)", el.urine_appearance],
    ["Miccion - Volumen", el.urine_volume],
    ["Miccion - Veces", el.urine_times],
    ["Miccion - Cantidad (ml)", el.urine_amount_ml],
    [
      "Evacuacion",
      el.stool_present === true ? "SI" : el.stool_present === false ? "NO" : "",
    ],
    ["Evacuacion - Veces", el.stool_present === true ? el.stool_times : ""],
    ["Evacuacion - Bristol", el.stool_present === true && el.bristol ? `Tipo ${el.bristol}` : ""],
    ["Evacuacion - Consistencia", el.stool_present === true ? el.stool_consistency : ""],
    ["Evacuacion - Aspecto", el.stool_present === true ? el.stool_appearance : ""],
    ["Evacuacion - Tipo", el.stool_present === true ? el.stool_method : ""],
  ].filter(([, val]) => val);
  if (elRows.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.setTextColor(...BRAND.muted);
    doc.text("(sin registro)", left, y + 4);
    y += 9;
  } else {
    autoTable(doc, {
      startY: y + 2,
      body: elRows,
      theme: "grid",
      bodyStyles: { fontSize: 9, textColor: BRAND.ink, cellPadding: 1.8 },
      columnStyles: {
        0: { cellWidth: 56, fontStyle: "bold", fillColor: [241, 245, 249], textColor: BRAND.muted },
        1: { cellWidth: "auto" },
      },
      margin: { left, right: 12 },
    });
    y = doc.lastAutoTable.finalY + 5;
  }

  // Balance hidrico
  const wb = computeWaterBalance(closing);
  if (wb) {
    y = ensureSpace(doc, y, 24, logoDataUrl);
    const cfgRgb = BALANCE_STATUS[wb.status];
    const labelRgb = hexToRgb(cfgRgb.color);
    const bgRgb = hexToRgb(cfgRgb.bg);
    const sign = wb.balance > 0 ? "+" : "";

    doc.setFillColor(...bgRgb);
    doc.setDrawColor(...labelRgb);
    doc.setLineWidth(0.5);
    doc.roundedRect(left, y, right - left, 18, 2, 2, "FD");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...labelRgb);
    doc.text("BALANCE HIDRICO", left + 3, y + 5);
    doc.text(cfgRgb.label.toUpperCase(), right - 3, y + 5, { align: "right" });

    const colW = (right - left) / 3;
    const drawCol = (col, title, value, valColor) => {
      const cx = left + colW * col + colW / 2;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      doc.setTextColor(...BRAND.muted);
      doc.text(title.toUpperCase(), cx, y + 10, { align: "center" });
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(...(valColor || BRAND.ink));
      doc.text(value, cx, y + 15.5, { align: "center" });
    };
    drawCol(0, "Ingresos", `${wb.intake} ml`);
    drawCol(1, "Egresos", `${wb.output} ml`);
    drawCol(2, "Balance", `${sign}${wb.balance} ml`, labelRgb);

    y += 22;
  }

  // Incidents
  y = ensureSpace(doc, y, 30, logoDataUrl);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...BRAND.muted);
  doc.text("INCIDENCIAS DEL TURNO", left, y);
  y += 5;
  const incs = closing.incidents || {};
  const incRows = Object.entries(INC_LABELS).map(([k, label]) => {
    const v = incs[k];
    const txt = v === true ? "SI" : v === false ? "NO" : "-";
    return [label, txt];
  });
  autoTable(doc, {
    startY: y,
    head: [["Tipo", "Ocurrio?"]],
    body: incRows,
    theme: "grid",
    headStyles: { fillColor: [241, 245, 249], textColor: BRAND.ink, fontSize: 8.5, fontStyle: "bold" },
    bodyStyles: { fontSize: 9, textColor: BRAND.ink, cellPadding: 1.8 },
    columnStyles: {
      0: { cellWidth: 60 },
      1: { cellWidth: 22, halign: "center", fontStyle: "bold" },
    },
    didParseCell: (data) => {
      if (data.section === "body" && data.column.index === 1) {
        if (data.cell.raw === "SI") data.cell.styles.textColor = [185, 28, 28];
        else if (data.cell.raw === "NO") data.cell.styles.textColor = [22, 101, 52];
      }
    },
    margin: { left, right: 12 },
  });
  y = doc.lastAutoTable.finalY + 4;

  if (closing.incidents_desc) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...BRAND.muted);
    doc.text("DESCRIPCION DE INCIDENCIAS", left, y);
    y += 4;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(...BRAND.ink);
    const descLines = doc.splitTextToSize(closing.incidents_desc, right - left);
    doc.text(descLines, left, y + 4);
    y += 4 + descLines.length * 4.2 + 4;
  }

  // Morse Fall Risk Scale
  if (isMorseFilled(closing.morse)) {
    y = ensureSpace(doc, y, 28, logoDataUrl);
    const mT = computeMorseTotal(closing.morse);
    const mL = morseLevel(mT);
    const labelRgb = hexToRgb(mL.color);
    const bgRgb = hexToRgb(mL.bg);
    doc.setFillColor(...bgRgb);
    doc.setDrawColor(...labelRgb);
    doc.setLineWidth(0.4);
    doc.roundedRect(left, y, right - left, 14, 2, 2, "FD");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...labelRgb);
    doc.text("ESCALA DE MORSE (RIESGO DE CAIDAS)", left + 3, y + 5);
    doc.setFontSize(13);
    doc.text(`${mT} - ${mL.label.toUpperCase()}`, right - 3, y + 9, { align: "right" });
    y += 16;
    // Optional: detail per item if you want it - kept compact for now
    const morseRows = MORSE_QUESTIONS.map((q) => {
      const v = closing.morse[q.key];
      const opt = q.opts.find((o) => o.score === v);
      return [q.title, opt ? `${opt.label} (${v})` : "-"];
    }).filter(([, val]) => val && val !== "-");
    if (morseRows.length > 0) {
      autoTable(doc, {
        startY: y,
        body: morseRows,
        theme: "grid",
        bodyStyles: { fontSize: 8, textColor: BRAND.ink, cellPadding: 1.5 },
        columnStyles: {
          0: { cellWidth: 76, fontStyle: "bold", fillColor: [241, 245, 249], textColor: BRAND.muted },
          1: { cellWidth: "auto" },
        },
        margin: { left, right: 12 },
      });
      y = doc.lastAutoTable.finalY + 5;
    }
  }

  // Sleep monitoring (24h)
  const sleepHours = Array.isArray(closing.sleep?.hours) ? [...closing.sleep.hours].sort((a, b) => a - b) : [];
  const sleepObs = (closing.sleep?.observations || "").trim();
  if (sleepHours.length > 0 || sleepObs) {
    y = ensureSpace(doc, y, 30, logoDataUrl);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...BRAND.muted);
    doc.text(`MONITOREO DE SUENO  (${sleepHours.length} h / 24)`, left, y);
    y += 4;

    // 24-cell grid: 12 cols x 2 rows
    const cols = 12;
    const cellW = (right - left) / cols;
    const cellH = 6;
    const sleepSet = new Set(sleepHours);
    doc.setFontSize(7);
    for (let h = 0; h < 24; h++) {
      const col = h % 12;
      const row = Math.floor(h / 12);
      const x = left + col * cellW;
      const yy = y + row * cellH;
      const active = sleepSet.has(h);
      if (active) {
        doc.setFillColor(29, 78, 216); // #1D4ED8
        doc.setDrawColor(30, 64, 175);
      } else {
        doc.setFillColor(241, 245, 249); // slate-100
        doc.setDrawColor(203, 213, 225);
      }
      doc.setLineWidth(0.2);
      doc.roundedRect(x + 0.3, yy + 0.3, cellW - 0.6, cellH - 0.6, 1, 1, "FD");
      doc.setTextColor(active ? 255 : 71, active ? 255 : 85, active ? 255 : 105);
      doc.setFont("helvetica", "bold");
      doc.text(String(h).padStart(2, "0"), x + cellW / 2, yy + cellH / 2 + 1.5, { align: "center" });
    }
    y += 2 * cellH + 3;

    // Time ranges summary (e.g. "00:00-03:00, 22:00-24:00")
    const ranges = [];
    let s = null, p = null;
    for (const h of sleepHours) {
      if (s === null) { s = h; p = h; continue; }
      if (h === p + 1) { p = h; continue; }
      ranges.push([s, p]); s = h; p = h;
    }
    if (s !== null) ranges.push([s, p]);
    if (ranges.length) {
      const fmt = (h) => `${String(h).padStart(2, "0")}:00`;
      const txt = ranges.map(([a, b]) => `${fmt(a)}-${fmt((b + 1) % 24)}`).join(", ");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(...BRAND.muted);
      const lines = doc.splitTextToSize(`Periodos: ${txt}`, right - left);
      doc.text(lines, left, y);
      y += lines.length * 3.8 + 1;
    }

    if (sleepObs) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      doc.setTextColor(...BRAND.muted);
      doc.text("OBSERVACIONES DEL SUENO", left, y);
      y += 4;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9.5);
      doc.setTextColor(...BRAND.ink);
      const obsLines = doc.splitTextToSize(sleepObs, right - left);
      doc.text(obsLines, left, y);
      y += obsLines.length * 4.2 + 2;
    }
    y += 2;
  }

  // Medications administered
  const medsGiven = Array.isArray(closing.medications_given) ? closing.medications_given : [];
  if (medsGiven.length > 0) {
    y = ensureSpace(doc, y, 30, logoDataUrl);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...BRAND.muted);
    doc.text(`MEDICAMENTOS ADMINISTRADOS  (${medsGiven.length})`, left, y);
    y += 4;
    const sortedMeds = [...medsGiven].sort((a, b) => (a.time || "").localeCompare(b.time || ""));
    autoTable(doc, {
      startY: y,
      head: [["Hora", "Medicamento", "Dosis", "Via", "Tipo", "Adm. por", "Obs."]],
      body: sortedMeds.map((m) => [
        m.time || "--:--",
        m.name || "-",
        m.dose || "-",
        m.via || "-",
        m.indication === "prn" ? "PRN" : m.indication === "rx" ? "Rx" : "-",
        m.administered_by || "-",
        m.observations || "",
      ]),
      theme: "grid",
      headStyles: { fillColor: BRAND.blue, textColor: BRAND.white, fontSize: 8 },
      bodyStyles: { fontSize: 8, textColor: BRAND.ink, valign: "top" },
      columnStyles: {
        0: { cellWidth: 14, halign: "center", fontStyle: "bold" },
        1: { cellWidth: 40, fontStyle: "bold" },
        2: { cellWidth: 22 },
        3: { cellWidth: 24 },
        4: { cellWidth: 12, halign: "center" },
        5: { cellWidth: 30 },
        6: { cellWidth: "auto" },
      },
      margin: { left, right: 12 },
    });
    y = doc.lastAutoTable.finalY + 4;
  }

  // Peritoneal Dialysis (DPCA) - bitácora del turno
  const pd = closing.peritoneal_dialysis || {};
  const pdEx = Array.isArray(pd.exchanges) ? pd.exchanges : [];
  if (pdEx.length > 0 || (pd.morning_weight_kg || "").trim() || (pd.observations || "").trim()) {
    y = ensureSpace(doc, y, 30, logoDataUrl);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...BRAND.muted);
    doc.text(`DIALISIS PERITONEAL (DPCA)  (${pdEx.length} recambios)`, left, y);
    y += 4;

    if ((pd.morning_weight_kg || "").trim()) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(...BRAND.ink);
      doc.text(`Peso corporal matutino: ${pd.morning_weight_kg} kg`, left, y);
      y += 4;
    }

    if (pdEx.length > 0) {
      const CONC_LABEL = {
        "1.5": "1.5%", "2.5": "2.5%", "4.25": "4.25%", icodextrina: "Icodex.", otra: "Otra",
      };
      const APP_LABEL = {
        claro: "Claro", turbio: "Turbio", hematico: "Hematico", fibrina: "Fibrina",
      };
      const sortedEx = [...pdEx].sort(
        (a, b) => (a.number || 0) - (b.number || 0) || (a.start_time || "").localeCompare(b.start_time || "")
      );
      let totalInf = 0;
      let totalDrn = 0;
      const body = sortedEx.map((e) => {
        const inf = Number(e.infused_ml) || 0;
        const drn = Number(e.drained_ml) || 0;
        const uf = inf || drn ? drn - inf : "";
        totalInf += inf;
        totalDrn += drn;
        return [
          String(e.number || "?"),
          e.start_time || "--:--",
          CONC_LABEL[e.concentration] || (e.concentration || "-"),
          inf || "-",
          drn || "-",
          uf === "" ? "-" : (uf > 0 ? `+${uf}` : String(uf)),
          APP_LABEL[e.appearance] || (e.appearance || "-"),
          e.notes || "",
        ];
      });
      const ufTotal = totalDrn - totalInf;
      autoTable(doc, {
        startY: y,
        head: [["#", "Hora", "Bolsa", "Infund.", "Drenado", "UF", "Aspecto", "Notas"]],
        body,
        foot: [[
          "",
          "",
          { content: "Subtotal turno", styles: { fontStyle: "bold", halign: "right" } },
          { content: String(totalInf), styles: { fontStyle: "bold" } },
          { content: String(totalDrn), styles: { fontStyle: "bold" } },
          {
            content: ufTotal > 0 ? `+${ufTotal}` : String(ufTotal),
            styles: {
              fontStyle: "bold",
              textColor: ufTotal < 0 ? [153, 27, 27] : [22, 101, 52],
            },
          },
          "",
          "",
        ]],
        theme: "grid",
        headStyles: { fillColor: BRAND.blue, textColor: BRAND.white, fontSize: 8 },
        bodyStyles: { fontSize: 8, textColor: BRAND.ink, valign: "top" },
        footStyles: { fillColor: [241, 245, 249], fontSize: 8, textColor: BRAND.ink },
        columnStyles: {
          0: { cellWidth: 8, halign: "center", fontStyle: "bold" },
          1: { cellWidth: 14, halign: "center" },
          2: { cellWidth: 18, halign: "center" },
          3: { cellWidth: 16, halign: "right" },
          4: { cellWidth: 16, halign: "right" },
          5: { cellWidth: 14, halign: "right", fontStyle: "bold" },
          6: { cellWidth: 22, halign: "center" },
          7: { cellWidth: "auto" },
        },
        didParseCell: (data) => {
          if (data.section === "body" && data.column.index === 6) {
            const appraw = String(data.cell.raw || "").toLowerCase();
            if (appraw && appraw !== "claro" && appraw !== "-") {
              data.cell.styles.fillColor = [254, 226, 226];
              data.cell.styles.textColor = [153, 27, 27];
              data.cell.styles.fontStyle = "bold";
            }
          }
        },
        margin: { left, right: 12 },
      });
      y = doc.lastAutoTable.finalY + 4;
    }

    if ((pd.observations || "").trim()) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(...BRAND.ink);
      const obs = doc.splitTextToSize(`Obs. DPCA: ${pd.observations.trim()}`, right - left);
      doc.text(obs, left, y);
      y += obs.length * 4.5 + 2;
    }
  }

  // Gastrostomia (PEG) - bitácora de alimentación enteral
  const peg = closing.gastrostomy || {};
  const pegFeed = Array.isArray(peg.feedings) ? peg.feedings : [];
  const pegTube = peg.tube_care || {};
  const pegHasTube =
    (pegTube.stoma_status || []).length > 0 ||
    (pegTube.external_length_cm || "").trim() ||
    pegTube.patency ||
    pegTube.tube_rotated != null ||
    pegTube.stoma_cleaned != null ||
    (pegTube.notes || "").trim();
  if (pegFeed.length > 0 || pegHasTube || (peg.weight_kg || "").trim() || (peg.observations || "").trim()) {
    y = ensureSpace(doc, y, 30, logoDataUrl);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...BRAND.muted);
    doc.text(`GASTROSTOMIA (PEG) - ALIMENTACION POR SONDA  (${pegFeed.length} tomas)`, left, y);
    y += 4;

    if ((peg.weight_kg || "").trim()) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(...BRAND.ink);
      doc.text(`Peso del paciente: ${peg.weight_kg} kg`, left, y);
      y += 4;
    }

    if (pegFeed.length > 0) {
      const METHOD_SHORT = { bolo: "Bolo", gravedad: "Gravedad", bomba: "Bomba" };
      const TOL_SHORT = {
        bien: "Bien", nausea: "Nausea", vomito: "Vomito",
        regurgitacion: "Regurgit.", distension: "Distension", diarrea: "Diarrea",
      };
      const sortedFeed = [...pegFeed].sort(
        (a, b) => (a.number || 0) - (b.number || 0) || (a.time || "").localeCompare(b.time || "")
      );
      let totalFormula = 0;
      let totalWater = 0;
      const body = sortedFeed.map((f) => {
        const vol = Number(f.volume_ml) || 0;
        const water = (Number(f.flush_before_ml) || 0) + (Number(f.flush_after_ml) || 0);
        totalFormula += vol;
        totalWater += water;
        return [
          String(f.number || "?"),
          f.time || "--:--",
          f.formula_type || "-",
          vol || "-",
          METHOD_SHORT[f.method] || (f.method || "-"),
          f.residual_ml == null || f.residual_ml === "" ? "-" : String(f.residual_ml),
          water || "-",
          TOL_SHORT[f.tolerance] || (f.tolerance || "-"),
          f.notes || "",
        ];
      });
      autoTable(doc, {
        startY: y,
        head: [["#", "Hora", "Formula", "Vol ml", "Metodo", "Residuo", "Agua ml", "Tolerancia", "Notas"]],
        body,
        foot: [[
          "",
          "",
          { content: "Total turno", styles: { fontStyle: "bold", halign: "right" } },
          { content: String(totalFormula), styles: { fontStyle: "bold" } },
          "",
          "",
          { content: String(totalWater), styles: { fontStyle: "bold" } },
          "",
          "",
        ]],
        theme: "grid",
        headStyles: { fillColor: BRAND.blue, textColor: BRAND.white, fontSize: 8 },
        bodyStyles: { fontSize: 8, textColor: BRAND.ink, valign: "top" },
        footStyles: { fillColor: [241, 245, 249], fontSize: 8, textColor: BRAND.ink },
        columnStyles: {
          0: { cellWidth: 8, halign: "center", fontStyle: "bold" },
          1: { cellWidth: 14, halign: "center" },
          2: { cellWidth: 28 },
          3: { cellWidth: 14, halign: "right" },
          4: { cellWidth: 18, halign: "center" },
          5: { cellWidth: 15, halign: "right" },
          6: { cellWidth: 15, halign: "right" },
          7: { cellWidth: 20, halign: "center" },
          8: { cellWidth: "auto" },
        },
        didParseCell: (data) => {
          if (data.section === "body" && data.column.index === 7) {
            const raw = String(data.cell.raw || "").toLowerCase();
            if (raw && raw !== "bien" && raw !== "-") {
              data.cell.styles.fillColor = [254, 226, 226];
              data.cell.styles.textColor = [153, 27, 27];
              data.cell.styles.fontStyle = "bold";
            }
          }
          if (data.section === "body" && data.column.index === 5) {
            const n = parseInt(String(data.cell.raw), 10);
            if (Number.isFinite(n) && n > 200) {
              data.cell.styles.fillColor = [254, 226, 226];
              data.cell.styles.textColor = [153, 27, 27];
              data.cell.styles.fontStyle = "bold";
            }
          }
        },
        margin: { left, right: 12 },
      });
      y = doc.lastAutoTable.finalY + 4;
    }

    if (pegHasTube) {
      const STOMA_SHORT = {
        sano: "Sano", enrojecido: "Enrojecido", granuloma: "Granuloma",
        fuga: "Fuga periestomal", secrecion: "Secrecion serosa",
        secrecion_purulenta: "Secrecion purulenta", dolor: "Dolor",
      };
      const PATENCY_SHORT = {
        permeable: "Permeable", lenta: "Pasa lento",
        obstruida: "OBSTRUIDA", salida_accidental: "SALIDA ACCIDENTAL",
      };
      y = ensureSpace(doc, y, 12, logoDataUrl);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(...BRAND.ink);
      const parts = [];
      if ((pegTube.stoma_status || []).length > 0) {
        parts.push(`Estoma: ${(pegTube.stoma_status || []).map((s) => STOMA_SHORT[s] || s).join(", ")}`);
      }
      if ((pegTube.external_length_cm || "").trim()) {
        parts.push(`Marca externa: ${pegTube.external_length_cm} cm`);
      }
      if (pegTube.patency) parts.push(`Permeabilidad: ${PATENCY_SHORT[pegTube.patency] || pegTube.patency}`);
      if (pegTube.tube_rotated != null) parts.push(`Sonda rotada: ${pegTube.tube_rotated ? "Si" : "No"}`);
      if (pegTube.stoma_cleaned != null) parts.push(`Limpieza estoma: ${pegTube.stoma_cleaned ? "Si" : "No"}`);
      const tubeLine = doc.splitTextToSize(`Cuidados de sonda: ${parts.join("  ·  ")}`, right - left);
      doc.text(tubeLine, left, y);
      y += tubeLine.length * 4.5 + 1;
      if ((pegTube.notes || "").trim()) {
        const tn = doc.splitTextToSize(`Notas sonda: ${pegTube.notes.trim()}`, right - left);
        doc.text(tn, left, y);
        y += tn.length * 4.5 + 1;
      }
      y += 1;
    }

    if ((peg.observations || "").trim()) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(...BRAND.ink);
      const obs = doc.splitTextToSize(`Obs. PEG: ${peg.observations.trim()}`, right - left);
      doc.text(obs, left, y);
      y += obs.length * 4.5 + 2;
    }
  }

  // Pending
  y = ensureSpace(doc, y, 20, logoDataUrl);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...BRAND.muted);
  doc.text("PENDIENTES PARA EL SIGUIENTE TURNO", left, y);
  y += 5;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...BRAND.ink);
  const pending = closing.pending || [];
  if (pending.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setTextColor(...BRAND.muted);
    doc.text("(ninguno)", left, y);
    y += 6;
  } else {
    pending.forEach((p) => {
      const ls = doc.splitTextToSize(`-  ${p}`, right - left);
      doc.text(ls, left, y);
      y += ls.length * 4.5;
    });
    y += 2;
  }

  // Observations
  if (closing.observations) {
    y = ensureSpace(doc, y, 24, logoDataUrl);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...BRAND.muted);
    doc.text("OBSERVACIONES GENERALES", left, y);
    y += 4;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...BRAND.ink);
    const obsLines = doc.splitTextToSize(closing.observations, right - left);
    doc.text(obsLines, left, y + 4);
    y += 4 + obsLines.length * 4.5 + 4;
  }

  // ============ Evidences (thumbnails grid) ============
  const evidences = Array.isArray(options.evidences) ? options.evidences : [];
  if (evidences.length > 0) {
    y = ensureSpace(doc, y, 24, logoDataUrl);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...BRAND.muted);
    doc.text(`EVIDENCIAS DEL TURNO (${evidences.length})`, left, y);
    y += 4;

    // 3 columns grid
    const cols = 3;
    const gap = 4;
    const cellW = (right - left - gap * (cols - 1)) / cols;
    const cellH = cellW * 0.85;            // image area
    const descH = 14;                       // text area below image
    const rowH = cellH + descH + 4;

    for (let i = 0; i < evidences.length; i++) {
      const col = i % cols;
      if (col === 0) {
        y = ensureSpace(doc, y, rowH + 2, logoDataUrl);
      }
      const x = left + col * (cellW + gap);

      // Frame
      doc.setDrawColor(...BRAND.border);
      doc.setFillColor(241, 245, 249);   // slate-100
      doc.setLineWidth(0.2);
      doc.roundedRect(x, y, cellW, cellH, 1.5, 1.5, "FD");

      const ev = evidences[i];
      if (ev.dataUrl) {
        try {
          // Detect image format from the data URL header so jsPDF picks the
          // right decoder. Falls back to JPEG which is the most permissive.
          const m = /^data:image\/([a-zA-Z]+);/i.exec(ev.dataUrl);
          let fmt = (m ? m[1] : "jpeg").toUpperCase();
          if (fmt === "JPG") fmt = "JPEG";
          if (!["JPEG", "PNG", "WEBP"].includes(fmt)) fmt = "JPEG";
          // Letterbox the image to fit the cell without distortion.
          const props = doc.getImageProperties(ev.dataUrl);
          const ratio = props.width / props.height;
          let iw = cellW - 2;
          let ih = iw / ratio;
          if (ih > cellH - 2) {
            ih = cellH - 2;
            iw = ih * ratio;
          }
          const ix = x + (cellW - iw) / 2;
          const iy = y + (cellH - ih) / 2;
          doc.addImage(ev.dataUrl, fmt, ix, iy, iw, ih, undefined, "FAST");
        } catch (e) {
          // fall through to placeholder
          doc.setFont("helvetica", "italic");
          doc.setFontSize(8);
          doc.setTextColor(...BRAND.muted);
          doc.text("(imagen no disponible)", x + cellW / 2, y + cellH / 2, { align: "center" });
        }
      } else if (ev.kind === "video") {
        // Video placeholder
        doc.setFillColor(30, 64, 175);
        doc.roundedRect(x + cellW / 2 - 10, y + cellH / 2 - 5, 20, 10, 2, 2, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.setTextColor(...BRAND.white);
        doc.text("VIDEO", x + cellW / 2, y + cellH / 2 + 1, { align: "center" });
      } else {
        doc.setFont("helvetica", "italic");
        doc.setFontSize(8);
        doc.setTextColor(...BRAND.muted);
        doc.text("(sin vista previa)", x + cellW / 2, y + cellH / 2, { align: "center" });
      }

      // Description below
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.5);
      doc.setTextColor(...BRAND.ink);
      const desc = ev.description || ev.original_filename || "(sin descripcion)";
      const descLines = doc.splitTextToSize(desc, cellW);
      doc.text(descLines.slice(0, 2), x, y + cellH + 4);

      // Meta (uploader + date)
      doc.setFont("helvetica", "normal");
      doc.setFontSize(6.5);
      doc.setTextColor(...BRAND.muted);
      const meta = `${ev.uploaded_by_name || ev.uploaded_by_email || "-"}  ${(ev.uploaded_at || "").slice(0, 16).replace("T", " ")}`;
      doc.text(meta, x, y + cellH + 11);

      if (col === cols - 1 || i === evidences.length - 1) {
        y += rowH;
      }
    }
    y += 2;
  }

  // ============ Signature ============
  y = ensureSpace(doc, y, 50, logoDataUrl);
  doc.setFillColor(...BRAND.light);
  doc.roundedRect(left, y, right - left, 38, 3, 3, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...BRAND.muted);
  doc.text("CIERRE DE TURNO", left + 4, y + 6);

  // Signature line - nurse
  const sigW = (right - left - 12) / 2;
  doc.setDrawColor(...BRAND.ink);
  doc.setLineWidth(0.3);
  doc.line(left + 4, y + 26, left + 4 + sigW, y + 26);
  doc.line(left + 12 + sigW, y + 26, right - 4, y + 26);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...BRAND.ink);
  doc.text(safe(closing.closing_nurse, "____________________"), left + 4 + sigW / 2, y + 24, {
    align: "center",
  });
  doc.text(safe(closing.closing_time, "____:____"), left + 12 + sigW + sigW / 2, y + 24, {
    align: "center",
  });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...BRAND.muted);
  doc.text("Enfermera responsable / Firma", left + 4 + sigW / 2, y + 31, { align: "center" });
  doc.text("Hora real de cierre", left + 12 + sigW + sigW / 2, y + 31, { align: "center" });

  // Footers on all pages
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    drawFooter(doc, i, pageCount);
  }

  // File name: include current generation time so multiple PDFs for the same
  // patient/date are distinguishable in WhatsApp / file managers
  const patient = (shift.patient_name || "paciente").replace(/[^a-z0-9]/gi, "_").toLowerCase();
  const date = shift.date || new Date().toISOString().slice(0, 10);
  const now = new Date();
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  const filename = `programa_${patient}_${date}_${hh}${mm}.pdf`;

  if (options.returnBlob) {
    return { blob: doc.output("blob"), filename };
  }
  doc.save(filename);
  return { filename };
}
